import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { Plus, Search } from 'lucide-react'

import type { ShopPostStatus } from '@/lib/api/shop-cms-api'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import { postStatusLabel } from '@/components/retail/cms/post-editor'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import {
    useShopPostCategories,
    useShopPosts,
} from '@/hooks/use-shop-posts-query'
import { useDebounced } from '@/hooks/use-debounced'
import { m } from '@/lib/paraglide/messages'

// Bán lẻ › Bài viết: tin tức website (thay Payload). Bài mới là nháp; bài đã
// đăng sửa tiếp thì có nháp chờ đăng.
export const Route = createFileRoute('/_app/_authed/_sidebar/retail/posts/')({
    component: PostsPage,
})

const ALL = 'all'
const PER_PAGE = 20

const fmtDate = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString('vi-VN') : '—'

export function PostsPage() {
    const { hasPermission } = useAuth()
    const [q, setQ] = useState('')
    const [status, setStatus] = useState(ALL)
    const [category, setCategory] = useState(ALL)
    const [page, setPage] = useState(1)
    const search = useDebounced(q.trim(), 300)
    const query = useShopPosts({
        q: search || undefined,
        status: status === ALL ? undefined : (status as ShopPostStatus),
        category_id: category === ALL ? undefined : category,
        page,
        per_page: PER_PAGE,
    })
    const categories = useShopPostCategories().data?.data ?? []
    const rows = query.data?.data ?? []
    const totalPages = query.data?.meta?.total_pages ?? 1

    return (
        <div className="space-y-4 page-pad">
            <ListPageHeader
                title={m.cmsPost_list_title()}
                subtitle={m.cmsPost_list_subtitle()}
                primaryActions={
                    hasPermission('shop_content.write') ? (
                        <Button size="sm" asChild>
                            <Link to="/retail/posts/new">
                                <Plus className="mr-1 size-4" />
                                {m.cmsPost_add()}
                            </Link>
                        </Button>
                    ) : undefined
                }
            />
            <div className="flex flex-wrap items-end gap-3">
                <div className="relative w-full max-w-sm">
                    <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        className="pl-8"
                        value={q}
                        aria-label={m.cmsPost_search()}
                        placeholder={m.cmsPost_search()}
                        onChange={(e) => {
                            setQ(e.target.value)
                            setPage(1)
                        }}
                    />
                </div>
                <Select
                    value={status}
                    onValueChange={(v) => {
                        setStatus(v)
                        setPage(1)
                    }}
                >
                    <SelectTrigger
                        className="w-44"
                        aria-label={m.cmsPost_filter_status()}
                    >
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ALL}>
                            {m.cmsPost_filter_all_status()}
                        </SelectItem>
                        <SelectItem value="published">
                            {m.cmsPost_status_published()}
                        </SelectItem>
                        <SelectItem value="draft">
                            {m.cmsPost_status_draft()}
                        </SelectItem>
                    </SelectContent>
                </Select>
                <Select
                    value={category}
                    onValueChange={(v) => {
                        setCategory(v)
                        setPage(1)
                    }}
                >
                    <SelectTrigger
                        className="w-52"
                        aria-label={m.cmsPost_category()}
                    >
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ALL}>
                            {m.cmsPost_filter_all_categories()}
                        </SelectItem>
                        {categories.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                                {c.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            <div className="overflow-x-auto rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.cmsPost_title()}</TableHead>
                            <TableHead>{m.cmsPost_category()}</TableHead>
                            <TableHead>{m.cmsPost_status()}</TableHead>
                            <TableHead>{m.cmsPost_published_at()}</TableHead>
                            <TableHead>{m.cmsPost_updated_at()}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {query.isError && (
                            <TableRow>
                                <TableCell
                                    colSpan={5}
                                    className="text-destructive"
                                    role="alert"
                                >
                                    {m.cmsPost_load_failed()}
                                </TableCell>
                            </TableRow>
                        )}
                        {!query.isLoading &&
                            !query.isError &&
                            rows.length === 0 && (
                                <TableRow>
                                    <TableCell
                                        colSpan={5}
                                        className="text-muted-foreground"
                                    >
                                        {m.cmsPost_empty()}
                                    </TableCell>
                                </TableRow>
                            )}
                        {rows.map((p) => (
                            <TableRow key={p.id}>
                                <TableCell className="max-w-md">
                                    <Link
                                        to="/retail/posts/$postId"
                                        params={{ postId: p.id }}
                                        className="font-medium text-primary hover:underline"
                                    >
                                        {p.title}
                                    </Link>
                                    {p.featured && (
                                        <Badge
                                            variant="outline"
                                            className="ml-2"
                                        >
                                            {m.cmsPost_featured_badge()}
                                        </Badge>
                                    )}
                                    <p className="truncate text-xs text-muted-foreground">
                                        /{p.slug}
                                    </p>
                                </TableCell>
                                <TableCell>{p.category_label ?? '—'}</TableCell>
                                <TableCell>
                                    <Badge
                                        variant={
                                            p.status === 'published'
                                                ? 'default'
                                                : 'secondary'
                                        }
                                    >
                                        {postStatusLabel(p.status, p.has_draft)}
                                    </Badge>
                                </TableCell>
                                <TableCell>{fmtDate(p.published_at)}</TableCell>
                                <TableCell>{fmtDate(p.updated_at)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
            {totalPages > 1 && (
                <div className="flex items-center justify-end gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={page <= 1}
                        onClick={() => setPage(page - 1)}
                    >
                        {m.cmsPost_prev()}
                    </Button>
                    <span className="text-sm">
                        {m.cmsPost_page({
                            page: String(page),
                            total: String(totalPages),
                        })}
                    </span>
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={page >= totalPages}
                        onClick={() => setPage(page + 1)}
                    >
                        {m.cmsPost_next()}
                    </Button>
                </div>
            )}
        </div>
    )
}
